import React, { ReactNode } from 'react';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

const DEFAULT_STARTED = (
  <span>
    You've done some great work on this section so far! Let's complete it!
  </span>
);

const DEFAULT_COMPLETED = (
  <span>
    Great job! You completed this section already. Let us know if you want to
    review your info or make any changes.
  </span>
);

const DEFAULT_LAST_WORKED_ON = (sectionName: string) => (
  <span>
    You were last working on your {sectionName} section. Let's keep going!
  </span>
);

const DEFAULT_NOT_STARTED = (sectionName: string) => (
  <span>
    You haven't started your {sectionName} section yet. Let's get started!
  </span>
);

export type ReportingTaskState =
  | 'STARTED'
  | 'LAST_WORKED_ON'
  | 'COMPLETED'
  | 'NEW_REPORT'
  | 'NOT_STARTED';

export const reportingTaskText: {
  [x in Exclude<FeedbackSectionId, 'allocation' | 'custom_questions'>]: {
    [y in ReportingTaskState]: ReactNode;
  };
} = {
  finalize_report: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Review and Submit'),
    NOT_STARTED: 'Looks like your report is ready to be submitted.',
    STARTED: 'Looks like your report is ready to be submitted.',
    NEW_REPORT:
      'Once you have completed all the report sections, you may able to submit the report here.',
  },
  interim_report: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Review and Submit'),
    NOT_STARTED: 'Looks like your report is ready to be submitted.',
    STARTED: 'Looks like your report is ready to be submitted.',
    NEW_REPORT:
      'Once you have completed all the report sections, you may able to submit the report here.',
  },
  location: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Location'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Location'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        In the location section, you are able to select the locations that are
        relevant to your program.
      </span>
    ),
  },
  beneficiaries: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Beneficiaries'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Beneficiaries'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        We will guide you on how to select the right beneficiaries of your
        program.
      </span>
    ),
  },
  budget: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Budget'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Budget'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        We will guide you on how to come up with the correct estimation for the
        budget.
      </span>
    ),
  },
  dates: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Dates'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Dates'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        Let&apos;s pick dates that are relevant to the reporting year.
      </span>
    ),
  },
  activities: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Activities'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Activities'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>Let&apos;s get started on your report's activities.</span>
    ),
  },
  description: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Description'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Description'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        Let&apos;s get started on your report with a quick description of the
        work your program does.
      </span>
    ),
  },
  narrative: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Narrative'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Narrative'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        Let&apos;s get started on your report with a quick narrative of the work
        your program does.
      </span>
    ),
  },
  impact_summary: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Impact Model Builder'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Impact Model Builder'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>Let&apos;s get started on your report's impact model builder.</span>
    ),
  },
  indicator_details: {
    COMPLETED: DEFAULT_COMPLETED,
    LAST_WORKED_ON: DEFAULT_LAST_WORKED_ON('Impact Model Details'),
    NOT_STARTED: DEFAULT_NOT_STARTED('Impact Model Details'),
    STARTED: DEFAULT_STARTED,
    NEW_REPORT: (
      <span>
        Let&apos;s get you started on the details of the indicators you will
        select the impact summary. Note: Impact Model Builder section is
        required before starting on the details.
      </span>
    ),
  },
};
